import { Loader2, Mail, Send } from "lucide-react";

const REASON_LABELS = {
  revenue_under_20k: "Revenue < $20k",
  credit_under_500: "Credit < 500",
  liens_or_bankruptcy: "Liens / BK",
  default_without_zbl: "Default, no ZBL",
  underwriting: "Underwriting",
};

export default function WaitlistCard({ entry, totalSteps, busy, onSendNext }) {
  const step = entry.sequence_step || 0;
  const key = entry.email.split("@")[0];
  const done = step >= totalSteps;
  const eligible = !done && !entry.converted_at && !entry.unsubscribed;
  const lastFailed = entry.emails_sent?.at(-1)?.status === "failed";

  return (
    <div className="border border-white/10 rounded-sm p-4 bg-white/[0.02] flex flex-col" data-testid={`waitlist-entry-${key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-zinc-200 truncate">{entry.name || "Unnamed"}</p>
          <p className="text-xs text-amber-400/80 mt-0.5 truncate">{entry.email}</p>
        </div>
        {entry.converted_at ? (
          <span className="text-[10px] font-mono-d tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-400 bg-emerald-500/10" data-testid={`waitlist-converted-${key}`}>Converted</span>
        ) : entry.unsubscribed ? (
          <span className="text-[10px] font-mono-d tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border border-white/15 text-zinc-500" data-testid={`waitlist-unsubscribed-${key}`}>Opted out</span>
        ) : (
          <span className="text-[10px] font-mono-d tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border border-rose-500/30 text-rose-300/90">
            {REASON_LABELS[entry.note] || "Underwriting"}
          </span>
        )}
      </div>

      {(entry.monthly_revenue || entry.credit_score) && (
        <p className="text-xs text-zinc-500 mt-2 font-mono-d">
          {entry.monthly_revenue ? `$${Number(entry.monthly_revenue).toLocaleString()}/mo` : ""}
          {entry.monthly_revenue && entry.credit_score ? " · " : ""}
          {entry.credit_score ? `FICO ${entry.credit_score}` : ""}
        </p>
      )}

      <div className="mt-3 flex items-center gap-1.5" data-testid={`waitlist-sequence-${key}`}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span key={i} className={`h-1 flex-1 rounded-full ${i < step ? "bg-amber-500" : "bg-white/10"}`} />
        ))}
        <span className="ml-2 text-[10px] font-mono-d text-zinc-500 whitespace-nowrap">
          <Mail className="w-3 h-3 inline -mt-0.5 mr-1" />{step}/{totalSteps}
        </span>
      </div>
      <p className="text-[11px] text-zinc-600 mt-1.5 font-mono-d">
        {entry.converted_at
          ? `Re-qualified ${new Date(entry.converted_at).toLocaleDateString()}`
          : done
            ? "Sequence complete"
            : lastFailed
              ? "Last send failed · retrying in 24h"
              : entry.next_send_at
                ? `Next email ${new Date(entry.next_send_at).toLocaleDateString()}`
                : `Joined ${new Date(entry.created_at).toLocaleDateString()}`}
      </p>

      {eligible && (
        <button
          onClick={() => onSendNext(entry.id)}
          disabled={busy !== null}
          data-testid={`waitlist-send-next-${key}`}
          className="mt-3 inline-flex items-center justify-center gap-1.5 text-[11px] font-mono-d tracking-[0.1em] uppercase border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        >
          {busy === entry.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Send email {step + 1} now
        </button>
      )}
    </div>
  );
}
